import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFavorites } from '../hooks/useFavorites';

/**
 * FavoritesPage Component
 * Dedicated page showing all saved favorite vessels and locations
 * Organized in two tabs with simple list view
 */
function FavoritesPage() {
  const navigate = useNavigate();
  const { favorites, removeVessel, removeLocation } = useFavorites();
  const [activeTab, setActiveTab] = useState<'vessels' | 'locations'>('vessels');

  const handleVesselClick = (registreringsmerke: string, vesselName: string) => {
    navigate(`/vessel/${registreringsmerke}`, {
      state: {
        vessel_name: vesselName,
        vessel_display: vesselName,
        registreringsmerke: registreringsmerke,
      },
    });
  };

  const handleLocationClick = (locationId: string, locationName: string, locationType: string) => {
    navigate(`/location/${locationId}`, {
      state: {
        location_id: locationId,
        location_name: locationName,
        type: locationType,
      },
    });
  };

  const handleRemoveVessel = (e: React.MouseEvent, registreringsmerke: string) => {
    e.stopPropagation();
    removeVessel(registreringsmerke);
  };

  const handleRemoveLocation = (e: React.MouseEvent, locationId: string) => {
    e.stopPropagation();
    removeLocation(locationId);
  };

  return (
    <div style={{ maxWidth: '800px', margin: '40px auto', padding: '0 20px' }}>
      <h1 style={{ textAlign: 'center', marginBottom: '40px', fontSize: '2.5rem' }}>
        Mine favoritter
      </h1>

      {/* Tab Navigation */}
      <div
        style={{
          display: 'flex',
          gap: '0',
          marginBottom: '30px',
          borderBottom: '2px solid #333',
        }}
      >
        <button
          onClick={() => setActiveTab('vessels')}
          style={{
            flex: 1,
            padding: '12px',
            border: 'none',
            backgroundColor: activeTab === 'vessels' ? '#333' : '#f5f5f5',
            color: activeTab === 'vessels' ? '#fff' : '#333',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: activeTab === 'vessels' ? 'bold' : 'normal',
            transition: 'all 0.3s ease',
          }}
        >
          Fartøy ({favorites.vessels.length})
        </button>
        <button
          onClick={() => setActiveTab('locations')}
          style={{
            flex: 1,
            padding: '12px',
            border: 'none',
            backgroundColor: activeTab === 'locations' ? '#333' : '#f5f5f5',
            color: activeTab === 'locations' ? '#fff' : '#333',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: activeTab === 'locations' ? 'bold' : 'normal',
            transition: 'all 0.3s ease',
          }}
        >
          Steder ({favorites.locations.length})
        </button>
      </div>

      {/* Vessels Tab */}
      {activeTab === 'vessels' && (
        <div>
          {favorites.vessels.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#666',
                fontSize: '1.1rem',
              }}
            >
              <p>Ingen favorittfartøy ennå.</p>
              <p style={{ marginTop: '10px', fontSize: '0.9rem' }}>
                Legg til ved å klikke på stjernen i søkeresultater eller på fartøysider.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {favorites.vessels.map((vessel) => (
                <div
                  key={vessel.registreringsmerke}
                  onClick={() =>
                    handleVesselClick(vessel.registreringsmerke, vessel.name)
                  }
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 12px',
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    ':hover': {
                      backgroundColor: '#f0f0f0',
                    },
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.backgroundColor =
                      '#f0f0f0';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.backgroundColor =
                      '#f9f9f9';
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: '1.2rem', marginRight: '8px', color: '#FFD700' }}>
                      ★
                    </span>
                    <span style={{ fontWeight: '500' }}>{vessel.name}</span>
                  </div>
                  <button
                    onClick={(e) =>
                      handleRemoveVessel(e, vessel.registreringsmerke)
                    }
                    style={{
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '6px 10px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      color: '#666',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                        '#ffebee';
                      (e.currentTarget as HTMLButtonElement).style.color = '#c62828';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                        '#fff';
                      (e.currentTarget as HTMLButtonElement).style.color = '#666';
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Locations Tab */}
      {activeTab === 'locations' && (
        <div>
          {favorites.locations.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#666',
                fontSize: '1.1rem',
              }}
            >
              <p>Ingen favorittsteder ennå.</p>
              <p style={{ marginTop: '10px', fontSize: '0.9rem' }}>
                Legg til ved å klikke på stjernen i søkeresultater eller på stedssider.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {favorites.locations.map((location) => (
                <div
                  key={location.id}
                  onClick={() =>
                    handleLocationClick(location.id, location.name, location.type)
                  }
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 12px',
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.backgroundColor =
                      '#f0f0f0';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.backgroundColor =
                      '#f9f9f9';
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: '1.2rem', marginRight: '8px', color: '#FFD700' }}>
                      ★
                    </span>
                    <span style={{ fontWeight: '500' }}>{location.name}</span>
                    <span style={{ color: '#999', marginLeft: '8px' }}>
                      ({location.type})
                    </span>
                  </div>
                  <button
                    onClick={(e) => handleRemoveLocation(e, location.id)}
                    style={{
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '6px 10px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      color: '#666',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                        '#ffebee';
                      (e.currentTarget as HTMLButtonElement).style.color = '#c62828';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                        '#fff';
                      (e.currentTarget as HTMLButtonElement).style.color = '#666';
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FavoritesPage;
